import lodash from 'lodash';
import { useMemo } from 'react';
import { ILocation } from '../../support/model/ILocation.ts';
import { useInstance } from '../../support/ioc/useInstance';
import { ISearchSelectOption, SearchableSelectField } from './SearchableSelectField.tsx';
import { LocationSearchService } from '../../map/location/LocationSearchService.ts';

export interface IAddressFieldProps {
    value?: string;
    onChange: (value: string, meta?: any) => void;
    readonly?: boolean;
    required?: boolean;
}

export const AddressField: React.FC<IAddressFieldProps> = ({ value, onChange, readonly, required }) => {
    const locationSearchService = useInstance(LocationSearchService);

    const loadOptions = useMemo(
        () => lodash.debounce(
            (text: string, callback: (options: ISearchSelectOption<ILocation>[]) => void) => {
                locationSearchService.search({ searchText: text, matchesToReturn: 5 }).then(locations => {
                    const options = locations.map(location => {
                        const address = formatAddress(location);
                        return { label: address, value: location };
                    });
                    callback(options);
                });
            },
            500,
            { leading: true, trailing: true, maxWait: 900 }
        ),
        [locationSearchService]
    );

    const formatAddress = (location: ILocation): string => {
        const { address: { street, city, state, zip } } = location;
        let formatted = [street, city, state].filter(p => Boolean(p)).join(', ');
        if (zip) {
            formatted += ` ${zip}`;
        }
        return formatted;
    };

    const onSelectOption = (option: any) => {
        const manuallyEntered = option && option.label && !option.value;
        onChange(option?.label, { manuallyEntered });
    };

    return (
        <SearchableSelectField
            label="Home Address"
            noInputMessage="Start typing to see a list of addresses..."
            readonly={readonly}
            required={required}
            allowCustom
            loadOptions={loadOptions}
            selected={value ? { label: value, value: undefined } : undefined}
            onChange={onSelectOption}
        />
    );
};
